import React, {useState, useRef, useEffect} from 'react';
import ReactMarkdown from 'react-markdown';
import { useLocation, useNavigate } from 'react-router-dom';
import './Chat.css';
import {getOpenAIResponse} from './Chatmodules/getOpenAIResponse';
import {saveOrUpdateChat} from './Chatmodules/chatService';
import {handleMessageSend, handleNewChat, handleSelectChat, handleEliminarChat, handleEditChatTitle, handleSaveRecipe} from './Chatmodules/chatHandlers';
import sendGreenIcon from './Icons/sendgreen.png';
import {useUserChats} from './hooks/useUserChats';
import {useClickOutside} from './hooks/useClickOutside';
import {usePlan} from './context/PlanContext';
import { useToast } from './context/ToastContext';
import AdSlot from './Monetization/AdSlot';

function Chat({userId}) {
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [chatList, setChatList] = useState([]);
    const [currentChatId, setCurrentChatId] = useState(null);
    const [currentChatTitle, setCurrentChatTitle] = useState("Nuevo Chat");
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [activeMenu, setActiveMenu] = useState(null);
    const [menuVisible, setMenuVisible] = useState(true);
    const menuRef = useRef(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const {incrementUsage, showUpgradeModal, isPremium, limits, usage} = usePlan();
    const { showToast } = useToast();

    // ¿El usuario ha alcanzado el límite mensual de mensajes?
    const atMessageLimit = !isPremium && (usage.messageCount || 0) >= limits.messagesPerMonth;

    useUserChats(userId, setChatList, setCurrentChatId, setMessages, setCurrentChatTitle);
    useClickOutside(menuRef, () => setActiveMenu(null));

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({behavior: 'smooth'});
       }
   }, [messages]);

    // ── Abrir chat de receta si viene de RecipeCard/SavedRecipeCard ──
    useEffect(() => {
        const { openChatId } = location.state || {};
        if (!openChatId || !chatList.length) return;

        const target = chatList.find(c => c.id === openChatId);
        if (target) {
            setCurrentChatId(target.id);
            setMessages(target.messages || []);
            setCurrentChatTitle(target.title || 'Receta');
            setMenuVisible(false); // <- ocultar sidebar en móvil
            // Limpiar state para que no se re-seleccione al volver
            window.history.replaceState({}, '');
        }
    }, [chatList, location.state]); // <- se dispara cuando chatList carga

    const sendMessage = () => {
      if (inputValue.trim() === '') return;
      if (atMessageLimit) return; // bloqueado por la UI, seguridad extra

      const valueToSend = inputValue;
      setInputValue('');
      if (inputRef.current) inputRef.current.blur();

      incrementUsage('chat');

      handleMessageSend(
        valueToSend,
        messages,           // <- NUEVO: pasa el historial actual directamente
        setMessages,
        userId,
        currentChatId,
        getOpenAIResponse,
        currentChatTitle,
        setChatList,
        setCurrentChatTitle
      ).catch(console.error);

      setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 300);
    };

    const newChat = async () => {
        // Básico: máximo 1 chat
        if (!isPremium && chatList.length >= limits.maxChats) {
            showUpgradeModal('newChat');
            return;
        }
        const newId = await handleNewChat(userId, setChatList, setCurrentChatId, saveOrUpdateChat);
        setCurrentChatId(newId);
        setMessages([]);
        setCurrentChatTitle("Nuevo Chat");

        // Cerrar menú y esconder dropdowns
        setMenuVisible(false);
        setActiveMenu(null);
   };

    const editTitle = async (chatId) => {
        await handleEditChatTitle(userId, chatId, newTitle, setChatList, setCurrentChatTitle, messages);
        setIsEditingTitle(false);
        setNewTitle('');
   };

    const saveRecipe = async (messageText) => {
        try {
            await handleSaveRecipe(userId, messageText);
            showToast('¡Receta guardada en tu recetario! 🎉', 'success');
        } catch {
            showToast('No se pudo guardar la receta. Inténtalo de nuevo.', 'error');
        }
   };

    const handleMenuToggle = (chatId) => {
        setActiveMenu(activeMenu === chatId ? null : chatId);
   };

    const handleMenuOptionClick = (action, chatId) => {
        if (action === 'delete') {
            handleEliminarChat(userId, chatId, setChatList, currentChatId, setCurrentChatId, setMessages, setCurrentChatTitle);
       } else if (action === 'edit') {
            setIsEditingTitle(true);
            setNewTitle(chatList.find(chat => chat.id === chatId).title);
       }
        setActiveMenu(null);
   };

    return (
        <div className="chat-page">
            <button
                className={`chat-menu-toggle${menuVisible ? ' right' : ' left'}`}
                onClick={() => setMenuVisible(v => !v)}
                aria-label="Mostrar/Ocultar menú"
            >
                <span />
                <span />
                <span />
            </button>
            <aside className={`chat-menu${menuVisible ? '' : ' hidden'}`}>
                <h4>Mis Chats</h4>
                <button
                    className={`new-chat-btn${!isPremium && chatList.length >= limits.maxChats ? ' new-chat-btn--locked' : ''}`}
                    onClick={newChat}
                    title={!isPremium && chatList.length >= limits.maxChats ? 'Premium: chats ilimitados' : 'Nuevo chat'}
                >
                    Nuevo Chat
                </button>
                <ul>
                    {chatList.map(chat => (
                        <li key={chat.id} onClick={() => handleSelectChat(chat.id, chatList, setCurrentChatId, setMessages, setCurrentChatTitle)}>
                            <div className="chat-title">
                                {chat.title}
                                <span className="menu-icon" onClick={(e) => {
                                    e.stopPropagation();
                                    handleMenuToggle(chat.id);
                               }}>⋮</span>
                            </div>
                            {activeMenu === chat.id && (
                                <div className="menu-dropdown" ref={menuRef}>
                                    <div onClick={(e) => {
                                        e.stopPropagation();
                                        handleMenuOptionClick('edit', chat.id);
                                   }}>Editar Título</div>
                                    <div onClick={(e) => {
                                        e.stopPropagation();
                                        handleMenuOptionClick('delete', chat.id);
                                   }}>Eliminar</div>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
                {isEditingTitle && (
                    <div className="edit-title-container">
                        <input
                            type="text"
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            placeholder="Nuevo Título"
                        />
                        <button onClick={() => editTitle(currentChatId)}>Guardar</button>
                    </div>
                )}
            </aside>
            <div className={`chat-container${menuVisible ? '' : ' full'}`}>
                <h2>{currentChatTitle}</h2>
                <div className="messages">
                    {messages.map((message, index) => {
  // Normalizar el texto para evitar errores si message.text no es string
  const text = typeof message.text === "string"
    ? message.text
    : (message.text?.content ?? message.text?.message ?? String(message.text ?? ""));

  return (
    <div key={message.id ?? index} className={message.sender}>
      {message.loading ? (
        <div className="assistant-loading">
          <span className="loading-dots" aria-hidden="true"><span></span></span>
        </div>
      ) : (
        <>
          <ReactMarkdown>{text}</ReactMarkdown>
          {message.sender === 'assistant' && typeof text === "string" && text.includes('**NOMBRE:**') && (
            <button className="save-recipe-btn" onClick={() => saveRecipe(text)}>
                Guardar Receta
            </button>
          )}
        </>
      )}
    </div>
  );
})}
                    <div ref={messagesEndRef} />
                </div>

                {/* Banner límite mensual de mensajes */}
                {atMessageLimit && (
                    <div className="chat-limit-banner">
                        <span>Has alcanzado el límite de <strong>{limits.messagesPerMonth} mensajes</strong> este mes.</span>
                        <span>Se renovará automáticamente el próximo mes.</span>
                        <button className="chat-limit-upgrade-btn" onClick={() => navigate('/pricing')}>
                            ✨ Mejorar a Premium
                        </button>
                    </div>
                )}

                {/* Banner de anuncio encima del input (solo usuarios básicos) */}
                <AdSlot slotId="chatBanner" format="horizontal" style={{ margin: '0 0 4px 0' }} />

                <div className="input-container">
                    <div className="input-wrapper">
                        <input
                            ref={inputRef}
                            type="text"
                            className="chat-input"
                            value={inputValue}
                            disabled={atMessageLimit}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder={atMessageLimit ? 'Límite mensual alcanzado...' : 'Escribe un mensaje...'}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    sendMessage();
                               }
                           }}
                        />
                        <img
                            src={sendGreenIcon}
                            className="comment-send-icon"
                            alt="Enviar"
                            onClick={sendMessage}
                            title="Enviar"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Chat;